import React from "react";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import moment from "moment";
import { Tooltip } from "primereact/tooltip";
import { canadianStatusDropdown } from "../../../appconfig/Settings";

interface EmploymentSectionProps {
  userData: any;
  errors: any;
  onInputChange: (e: any, name: string) => void;
  provinces: any[];
  handleLandlordChange: (field: string, value: any, index: number) => void;
  handleDependentChildChange: (field: string, value: any, index: number) => void;
  handleRemoveLandlord: (index: number) => void;
  handleAddLandlord: () => void;
  handleRemoveDependentChild: (index: number) => void;
  handleAddDependentChild: () => void;
  isMarried: boolean;
}

const EmploymentSection: React.FC<EmploymentSectionProps> = ({
  userData,
  errors,
  onInputChange,
  provinces,
  handleLandlordChange,
  handleRemoveLandlord,
  handleAddLandlord,
  handleDependentChildChange,
  handleRemoveDependentChild,
  handleAddDependentChild,
  isMarried,
}) => {
  return (
    <div className="form-section">
      <div className="section-title">
        <i className="pi pi-briefcase"></i>
        <h3>Employment & Income Details</h3>
      </div>

      <div className="grid p-fluid formgrid form-max-heigth">
        {/* Filling form first time */}
        <div className={userData?.first_time_form_filling_status === "0" ? "field col-12 md:col-4" : "field col-12 md:col-4"}>
          <label htmlFor="benefit_status">
            Filing tax in Canada for the first time
            <span className="required">*</span>
          </label>
          <div className="flex gap-3 mt-2">
            <div className="flex align-items-center">
              <RadioButton
                inputId="first_time_form_filling_status"
                name="first_time_form_filling_status"
                value="1"
                onChange={(e) => onInputChange(e, "first_time_form_filling_status")}
                checked={userData?.first_time_form_filling_status === "1"}
              />
              <label htmlFor="first_time_yes" className="ml-2">
                Yes
              </label>
            </div>
            <div className="flex align-items-center">
              <RadioButton
                inputId="first_time_form_filling_status"
                name="first_time_form_filling_status"
                value="0"
                onChange={(e) => onInputChange(e, "first_time_form_filling_status")}
                checked={userData?.first_time_form_filling_status === "0"}
              />
              <label htmlFor="first_time_no" className="ml-2">
                No
              </label>
            </div>
          </div>
          <small className="p-invalid-txt">{errors["first_time_form_filling_status"]}</small>
        </div>
        {/* form filling not first time */}
        {userData?.first_time_form_filling_status === "0" && (
          <div className={userData?.moved_from_province === "0" ? "field col-12 md:col-4" : "field col-12 md:col-8"}>
            <label htmlFor="moved_from_province">
              Moved from another province
              <span className="required">*</span>
            </label>
            <div className="flex gap-3 mt-2">
              <div className="flex align-items-center">
                <RadioButton
                  inputId="moved_from_province"
                  name="moved_from_province"
                  value="1"
                  onChange={(e) => onInputChange(e, "moved_from_province")}
                  checked={userData?.moved_from_province === "1"}
                />
                <label htmlFor="moved_from_province" className="ml-2">
                  Yes
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="moved_from_province"
                  name="moved_from_province"
                  value="0"
                  onChange={(e) => onInputChange(e, "moved_from_province")}
                  checked={userData?.moved_from_province === "0"}
                />
                <label htmlFor="moved_from_province" className="ml-2">
                  No
                </label>
              </div>
            </div>
            <small className="p-invalid-txt">{errors["moved_from_province"]}</small>
          </div>
        )}
        {/* income from home & date of entry */}
        {userData.first_time_form_filling_status === "1" && (
          <>
            {/* income from home country */}
            <div className="field col-12 md:col-4">
              <label htmlFor="income_from_home">
                Income From Home Country
                <span className="required">*</span>
                <>
                  <i
                    className="pi pi-info-circle"
                    data-pr-tooltip={`Convert to canadian dollar`}
                    data-pr-position="right"
                    style={{
                      marginLeft: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  ></i>
                  <Tooltip target=".pi-info-circle" />
                </>
              </label>
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">$</span>
                <InputNumber
                  value={userData?.income_from_home}
                  onChange={(e) => onInputChange(e, "income_from_home")}
                  className={errors["income_from_home"] && "p-invalid"}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                  placeholder="Enter income "
                />
              </div>
              <small className="p-invalid-txt">{errors["income_from_home"]}</small>
            </div>

            {/* Date of Entry */}
            <div className="field col-12 md:col-4">
              <label htmlFor="date_of_entry">
                Date of Entry <span className="required">*</span>
              </label>
              <Calendar
                value={userData.date_of_entry ? moment(userData.date_of_entry, "YYYY-MM-DD").toDate() : null}
                dateFormat="dd/mm/yy"
                onChange={(e) => onInputChange(e, "date_of_entry")}
                placeholder="Entry date"
                className={errors["date_of_entry"] && "p-invalid"}
                showIcon
              />
              <small className="p-invalid-txt">{errors["date_of_entry"]}</small>
            </div>
          </>
        )}
        {/* province from and province to based on first time and moved */}
        {userData?.first_time_form_filling_status === "0" && userData?.moved_from_province === "1" && (
          <>
            {/* movind date */}
            <div className="field col-12 md:col-4">
              <label htmlFor="moving_date">
                Moving Date <span className="required">*</span>
              </label>
              <Calendar
                value={userData.moving_date ? moment(userData.moving_date, "YYYY-MM-DD").toDate() : null}
                dateFormat="dd/mm/yy"
                onChange={(e) => onInputChange(e, "moving_date")}
                placeholder="Moving date"
                className={errors["moving_date"] ? "p-invalid" : ""}
                showIcon
              />
              <small className="p-invalid-txt">{errors["moving_date"]}</small>
            </div>

            {/* province from */}
            <div className="field col-12 md:col-4">
              <label htmlFor="province_from">
                Select Province From <span className="required">*</span>
              </label>
              <Dropdown
                value={userData.province_from || ""}
                onChange={(e) => onInputChange(e, "province_from")}
                options={provinces}
                placeholder="Select province From"
                className={errors["province_from"] ? "p-invalid" : ""}
              />
              <small className="p-invalid-txt">{errors["province_from"]}</small>
            </div>

            {/* province to */}
            <div className="field col-12 md:col-4">
              <label htmlFor="province_to">
                Select Province To <span className="required">*</span>
              </label>
              <Dropdown
                value={userData.province_to || ""}
                onChange={(e) => onInputChange(e, "province_to")}
                options={provinces}
                placeholder="Select province To"
                className={errors["province_to"] ? "p-invalid" : ""}
              />
              <small className="p-invalid-txt">{errors["province_to"]}</small>
            </div>
          </>
        )}
        {/* Rent Benefit */}
        {(userData?.province == "British Columbia" || userData?.province == "Manitoba") && (
          <div className="field col-12 md:col-12 md:mt-3">
            <label htmlFor="benefit_status">
              Rent Benefit - If Eligible - Do You Want To Apply
              <span className="required">*</span>
            </label>
            <div className="flex gap-3 mt-2">
              <div className="flex align-items-center">
                <RadioButton
                  inputId="benefit_status"
                  name="benefit_status"
                  value="1"
                  onChange={(e) => onInputChange(e, "benefit_status")}
                  checked={userData?.benefit_status === "1"}
                />
                <label htmlFor="benefit_yes" className="ml-2">
                  Yes
                </label>
              </div>
              <div className="flex align-items-center">
                <RadioButton
                  inputId="benefit_status"
                  name="benefit_status"
                  value="0"
                  onChange={(e) => onInputChange(e, "benefit_status")}
                  checked={userData?.benefit_status === "0"}
                />
                <label htmlFor="benefit_no" className="ml-2">
                  No
                </label>
              </div>
            </div>
            <small className="p-invalid-txt">{errors["benefit_status"]}</small>
          </div>
        )}
        {userData.benefit_status === "1" && (
          <>
            {/* Landlord Names */}
            <div className="field col-12 md:col-12">
              {(userData.landlords?.length ? userData.landlords : [{ name: "", amount: 0, months: "" }]).map((landlord: any, index: number) => (
                <div key={index} className="formgrid grid mb-2">
                  {/* Name Field */}
                  <div className="field col-12 md:col-4">
                    <label>
                      Landlords Name {index + 1}
                      <span className="required">*</span>
                    </label>
                    <InputText
                      value={landlord?.name}
                      onChange={(e) => handleLandlordChange("name", e.target.value, index)}
                      placeholder={`Name ${index + 1}`}
                      className={errors.landlords?.[index]?.name ? "p-invalid" : ""}
                    />

                    {errors.landlords?.[index]?.name && <small className="p-invalid-txt">{errors.landlords[index].name}</small>}
                  </div>

                  {/* Amount Field */}
                  <div className="field col-12 md:col-4">
                    <label>
                      Amount <span className="required">*</span>
                    </label>
                    <div className="p-inputgroup">
                      <span className="p-inputgroup-addon">$</span>
                      <InputNumber
                        value={landlord.amount || 0}
                        onChange={(e) => handleLandlordChange("amount", e.value, index)}
                        placeholder="Enter Amount"
                        className={errors.landlords?.[index]?.amount ? "p-invalid" : ""}
                        maxFractionDigits={2}
                        minFractionDigits={2}
                        mode="decimal"
                      />
                    </div>
                    {errors.landlords?.[index]?.amount && <small className="p-invalid-txt">{errors.landlords[index].amount}</small>}
                  </div>

                  {/* no of months */}
                  <div className="field col-12 md:col-3">
                    <label>
                      No of Months <span className="required">*</span>
                    </label>
                    <div className="p-inputgroup">
                      <InputText
                        value={landlord?.months}
                        onChange={(e) => handleLandlordChange("months", e?.target?.value, index)}
                        placeholder="Enter Number of Months"
                        className={errors.landlords?.[index]?.months ? "p-invalid" : ""}
                        keyfilter={"pint"}
                        maxLength={2}
                      />
                    </div>
                    {errors.landlords?.[index]?.months && <small className="p-invalid-txt">{errors.landlords[index].months}</small>}
                  </div>

                  {/* Remove Button */}
                  <div className="col-12 md:col-1 flex align-items-center mt-2">
                    {index > 0 && (
                      <Button
                        icon="pi pi-trash"
                        className="p-button-danger p-button-rounded p-button-sm"
                        onClick={() => handleRemoveLandlord(index)}
                      />
                    )}
                  </div>
                </div>
              ))}

              <Button
                label="Add Name"
                icon="pi pi-plus"
                className="p-button mt-2 add-more-btn"
                style={{ width: "auto" }}
                onClick={handleAddLandlord}
              />
            </div>
          </>
        )}
        <div className="formgrid w-full">
          {/* spouse employment and income detail */}
          {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") &&
            userData?.is_spouse_canadian_resident === "1" && (
              <>
                <hr />
                <div className="section-title mt-3">
                  <i className="pi pi-briefcase"></i>
                  <h3>Spouse Employment & Income Details</h3>
                </div>
                <div className="grid p-fluid formgrid">
                  {/* Spouse Filling form first time */}
                  <div
                    className={
                      userData?.spouse_first_time_form_filling_status === "1"
                        ? "field col-12 md:col-4"
                        : userData?.spouse_first_time_form_filling_status === "0"
                          ? "field col-12 md:col-4"
                          : "field col-12 md:col-4"
                    }
                  >
                    <label htmlFor="benefit_status">
                      Spouse filing taxes for first time
                      <span className="required">*</span>
                    </label>
                    <div className="flex gap-3 mt-2">
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="spouse_first_time_form_filling_status"
                          name="spouse_first_time_form_filling_status"
                          value="1"
                          onChange={(e) => onInputChange(e, "spouse_first_time_form_filling_status")}
                          checked={userData?.spouse_first_time_form_filling_status === "1"}
                        />
                        <label htmlFor="first_time_yes" className="ml-2">
                          Yes
                        </label>
                      </div>
                      <div className="flex align-items-center">
                        <RadioButton
                          inputId="spouse_first_time_form_filling_status"
                          name="spouse_first_time_form_filling_status"
                          value="0"
                          onChange={(e) => onInputChange(e, "spouse_first_time_form_filling_status")}
                          checked={userData?.spouse_first_time_form_filling_status === "0"}
                        />
                        <label htmlFor="first_time_no" className="ml-2">
                          No
                        </label>
                      </div>
                    </div>
                    <small className="p-invalid-txt">{errors["spouse_first_time_form_filling_status"]}</small>
                  </div>

                  {/* based on spouse not first time fields */}
                  {userData?.spouse_first_time_form_filling_status === "0" && (
                    <div
                      className={
                        userData?.spouse_moved_from_province === "0"
                          ? "field col-12 md:col-4"
                          : userData?.spouse_moved_from_province === "1"
                            ? "field col-12 md:col-8"
                            : "field col-12 md:col-4"
                      }
                    >
                      <label htmlFor="spouse_moved_from_province">
                        Moved From Province
                        <span className="required">*</span>
                      </label>
                      <div className="flex gap-3 mt-2">
                        <div className="flex align-items-center">
                          <RadioButton
                            inputId="spouse_moved_from_province"
                            name="spouse_moved_from_province"
                            value="1"
                            onChange={(e) => onInputChange(e, "spouse_moved_from_province")}
                            checked={userData?.spouse_moved_from_province === "1"}
                          />
                          <label htmlFor="spouse_moved_from_province" className="ml-2">
                            Yes
                          </label>
                        </div>
                        <div className="flex align-items-center">
                          <RadioButton
                            inputId="spouse_moved_from_province"
                            name="spouse_moved_from_province"
                            value="0"
                            onChange={(e) => onInputChange(e, "spouse_moved_from_province")}
                            checked={userData?.spouse_moved_from_province === "0"}
                          />
                          <label htmlFor="spouse_moved_from_province" className="ml-2">
                            No
                          </label>
                        </div>
                      </div>
                      <small className="p-invalid-txt">{errors["spouse_moved_from_province"]}</small>
                    </div>
                  )}

                  {/* province moving  */}
                  {userData?.spouse_first_time_form_filling_status === "0" && userData?.spouse_moved_from_province === "1" && (
                    <>
                      {/* Spouse Moving Date */}
                      <div className="field col-12 md:col-4">
                        <label htmlFor="spouse_moving_date">Moving Date</label>
                        <span className="required">*</span>
                        <div className="input-effect-div">
                          <Calendar
                            value={userData.spouse_moving_date ? moment(userData.spouse_moving_date, "YYYY-MM-DD").toDate() : null}
                            dateFormat="dd/mm/yy"
                            onChange={(e) => onInputChange(e, "spouse_moving_date")}
                            className={errors["spouse_moving_date"] ? "p-invalid" : ""}
                            placeholder="Enter Moving Date"
                            showIcon
                          />
                          <span className="focus-bg"></span>
                        </div>
                        <small className="p-invalid-txt">{errors["spouse_moving_date"]}</small>
                      </div>

                      {/* Spouse Province From */}
                      <div className="field md:col-4 col-12">
                        <label htmlFor="spouse_province_from">Select Province From</label>
                        <span className="required">*</span>
                        <Dropdown
                          value={userData.spouse_province_from || ""}
                          onChange={(e) => onInputChange(e, "spouse_province_from")}
                          options={provinces}
                          placeholder="Select Province From"
                          className={errors["spouse_province_from"] ? "p-invalid" : ""}
                        />
                        <small className="p-invalid-txt">{errors["spouse_province_from"]}</small>
                      </div>

                      {/* Spouse Province To */}
                      <div className="field md:col-4 col-12">
                        <label htmlFor="spouse_province_to">Select Province To</label>
                        <span className="required">*</span>
                        <Dropdown
                          value={userData.spouse_province_to || ""}
                          onChange={(e) => onInputChange(e, "spouse_province_to")}
                          options={provinces}
                          placeholder="Select Province To"
                          className={errors["spouse_province_to"] ? "p-invalid" : ""}
                        />
                        <small className="p-invalid-txt">{errors["spouse_province_to"]}</small>
                      </div>
                    </>
                  )}

                  {/* based on spouse filling form for first time */}
                  {userData?.spouse_first_time_form_filling_status === "1" && (
                    <>
                      {/* Spouse Date of Entry */}
                      <div className="field col-12 md:col-4">
                        <label htmlFor="spouse_date_of_entry">Date of Entry</label>
                        <span className="required">*</span>
                        <div className="input-effect-div">
                          <Calendar
                            value={userData.spouse_date_of_entry ? moment(userData.spouse_date_of_entry, "YYYY-MM-DD").toDate() : null}
                            dateFormat="dd/mm/yy"
                            onChange={(e) => onInputChange(e, "spouse_date_of_entry")}
                            placeholder="Enter Date of Entry"
                            className={errors["spouse_date_of_entry"] ? "p-invalid" : ""}
                            showIcon
                          />
                          <span className="focus-bg"></span>
                        </div>
                        <small className="p-invalid-txt">{errors["spouse_date_of_entry"]}</small>
                      </div>

                      {/* Spouse Income from Home */}
                      <div className="field col-12 md:col-4">
                        <label htmlFor="income_from_home">
                          Income From Home Country
                          <span className="required">*</span>
                          <>
                            <i
                              className="pi pi-info-circle"
                              data-pr-tooltip={`Convert to canadian dollar`}
                              data-pr-position="right"
                              style={{
                                marginLeft: "6px",
                                cursor: "pointer",
                                fontSize: "14px",
                              }}
                            ></i>
                            <Tooltip target=".pi-info-circle" />
                          </>
                        </label>
                        <div className="flex-1 input-effect-div p-inputgroup">
                          <span className="font-bold p-inputgroup-addon">$</span>
                          <InputNumber
                            value={userData.spouse_income_from_home}
                            onChange={(e) => onInputChange(e, "spouse_income_from_home")}
                            placeholder="Enter Income"
                            className={errors["spouse_income_from_home"] && "p-invalid"}
                            minFractionDigits={2}
                            maxFractionDigits={2}
                            mode="decimal"
                          />
                        </div>
                        <small className="p-invalid-txt">{errors["spouse_income_from_home"]}</small>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
        </div>

        {/* General Information */}
        <div className="formgrid w-full">
          <hr />
          <div className="section-title mt-3">
            <i className="pi pi-list"></i>
            <h3>General Information</h3>
          </div>

          <div className="grid p-fluid formgrid form-max-heigth">
            <div className="field col-12 md:col-4">
              <label htmlFor="canadian_status">
                Status in Canada <span className="required">*</span>
              </label>
              <Dropdown
                value={userData.canadian_status || ""}
                onChange={(e) => onInputChange(e, "canadian_status")}
                options={canadianStatusDropdown}
                placeholder="Select status"
                className={errors["canadian_status"] ? "p-invalid" : ""}
              />
              <small className="p-invalid-txt">{errors["canadian_status"]}</small>
            </div>

            {/* spouse status in canada */}
            {(userData?.dec_marrige_status === "Married" || userData?.dec_marrige_status === "Living common-law") &&
              userData?.is_spouse_canadian_resident == "1" && (
                <div className="field col-12 md:col-4">
                  <label htmlFor="spouse_canadian_status">
                    Spouse Status in Canada <span className="required">*</span>
                  </label>
                  <Dropdown
                    value={userData.spouse_canadian_status || ""}
                    onChange={(e) => onInputChange(e, "spouse_canadian_status")}
                    options={canadianStatusDropdown}
                    placeholder="Select status"
                    className={errors["spouse_canadian_status"] ? "p-invalid" : ""}
                  />
                  <small className="p-invalid-txt">{errors["spouse_canadian_status"]}</small>
                </div>
              )}

            {/* Dependent Child Section */}
            <div className="field col-12 mb-0">
              <label htmlFor="dependent_child">
                <span style={{ fontWeight: "600" }}>Dependent Child (IF LIVES IN CANADA WITH YOU)</span>
                <>
                  <i
                    className="pi pi-info-circle"
                    data-pr-tooltip={`If you have child, please enter below details.`}
                    data-pr-position="right"
                    style={{
                      marginLeft: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                    }}
                  ></i>
                  <Tooltip target=".pi-info-circle" />
                </>
              </label>
            </div>

            <div className="field col-12 md:col-12 mb-5">
              {(userData.dependentChildren?.length ? userData.dependentChildren : [{ child_name: "", child_dob: null }]).map(
                (child: any, index: number) => (
                  <div key={index} className="formgrid grid mb-2">
                    {/* Child Name Field */}
                    <div className="field col-12 md:col-4">
                      <label>
                        Child Name {index + 1}
                        <span className="required"></span>
                      </label>
                      <InputText
                        value={child?.child_name || ""}
                        onChange={(e) => handleDependentChildChange("child_name", e.target.value, index)}
                        placeholder={`Enter child name ${index + 1}`}
                        className={errors.dependentChildren?.[index]?.child_name ? "p-invalid" : ""}
                      />
                      {errors.dependentChildren?.[index]?.child_name && (
                        <small className="p-invalid-txt">{errors.dependentChildren[index].child_name}</small>
                      )}
                    </div>

                    {/* Child Date of Birth Field */}
                    <div className="field col-12 md:col-4">
                      <label>
                        Date of Birth <span className="required"></span>
                      </label>
                      <Calendar
                        value={child?.child_dob ? moment(child.child_dob, "YYYY-MM-DD").toDate() : null}
                        dateFormat="dd/mm/yy"
                        onChange={(e) => handleDependentChildChange("child_dob", e.value, index)}
                        placeholder="Select child date of birth"
                        showIcon
                        className={errors.dependentChildren?.[index]?.child_dob ? "p-invalid" : ""}
                      />
                      {errors.dependentChildren?.[index]?.child_dob && (
                        <small className="p-invalid-txt">{errors.dependentChildren[index].child_dob}</small>
                      )}
                    </div>

                    {/* Remove Button */}
                    <div className="col-12 md:col-1 flex align-items-center mt-2">
                      {index > 0 && (
                        <Button
                          icon="pi pi-trash"
                          className="p-button-danger p-button-rounded p-button-sm"
                          onClick={() => handleRemoveDependentChild(index)}
                        />
                      )}
                    </div>
                  </div>
                ),
              )}

              <Button
                label="Add Child"
                icon="pi pi-plus"
                className="p-button mt-2 add-more-btn"
                style={{ width: "auto" }}
                onClick={handleAddDependentChild}
              />
            </div>

            {/* child care expense  */}
            <div className="field col-12 md:col-4">
              <label htmlFor="child_care_expense">
                Child Care Expense
                <span className="required"></span>
              </label>
              <div className="flex-1 input-effect-div p-inputgroup">
                <span className="font-bold p-inputgroup-addon">$</span>
                <InputNumber
                  value={userData.child_care_expense}
                  onChange={(e) => onInputChange(e, "child_care_expense")}
                  placeholder="Enter Child Care Expense"
                  className={errors["child_care_expense"] && "p-invalid"}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                />
              </div>
              <small className="p-invalid-txt">{errors["child_care_expense"]}</small>
            </div>

            {/* Donation Amount */}
            <div className="field col-12 md:col-4">
              <label htmlFor="donation_amount">
                Donation Amount <span className="required"></span>
              </label>
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">$</span>
                <InputNumber
                  value={userData?.donation_amount}
                  onChange={(e) => onInputChange(e, "donation_amount")}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                  placeholder="Enter donation amount"
                />
              </div>
            </div>
            {/* Medical Expenses */}
            <div className="field col-12 md:col-4">
              <label htmlFor="medical_expense">
                Medical Expense Amount <span className="required"></span>
              </label>
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">$</span>
                <InputNumber
                  value={userData?.medical_expense}
                  onChange={(e) => onInputChange(e, "medical_expense")}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  mode="decimal"
                  placeholder="Enter medical Expense"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmploymentSection;
